/**
 * Shared body profile — one vault record per user that auto-fills the
 * health calculators (BMI, BMR, body fat, ideal weight, macro, water
 * intake). Values are stored canonically in METRIC units; the BMI hub's
 * imperial fields are derived on prefill.
 */

import type { ActivityLevel, Sex } from "./fitness";

export interface BodyProfile {
  version: 1;
  units?: "metric" | "imperial";
  heightCm?: number;
  weightKg?: number;
  age?: number;
  sex?: Sex;
  activity?: ActivityLevel;
  // Body-fat tape measurements (cm)
  neckCm?: number;
  waistCm?: number;
  hipCm?: number;
  // Other remembered inputs
  exerciseMinPerDay?: number;
  calorieTarget?: number;
  updatedAt: number;
}

/** Parse a controlled-input string into a positive finite number, else undefined. */
export function numField(value: string): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Merge a partial update into a profile, ignoring undefined fields. */
export function mergeProfile(
  profile: BodyProfile | null,
  update: Partial<BodyProfile>,
  now: number = Date.now(),
): BodyProfile {
  const base: BodyProfile = profile ?? { version: 1, updatedAt: now };
  const merged: BodyProfile = { ...base, updatedAt: now };
  for (const [key, value] of Object.entries(update)) {
    if (value !== undefined) {
      (merged as unknown as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

/** True when every defined field of `update` already matches `profile`. */
export function profileMatches(
  profile: BodyProfile | null,
  update: Partial<BodyProfile>,
): boolean {
  if (!profile) return Object.values(update).every((v) => v === undefined);
  return Object.entries(update).every(
    ([key, value]) =>
      value === undefined ||
      (profile as unknown as Record<string, unknown>)[key] === value,
  );
}

/** 178cm → { ft: 5, inch: 10 } (inch rounded, carrying 12" into feet). */
export function cmToFtIn(cm: number): { ft: number; inch: number } {
  const totalInches = cm / 2.54;
  let ft = Math.floor(totalInches / 12);
  let inch = Math.round(totalInches - ft * 12);
  if (inch === 12) {
    ft += 1;
    inch = 0;
  }
  return { ft, inch };
}

/** 70kg → 154.3lb (one decimal). */
export function kgToLb(kg: number): number {
  return Math.round((kg / 0.45359237) * 10) / 10;
}
